import jwt from 'jsonwebtoken';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import JWT from "../models/users/JWT";

/**
 * `local` sobrevive a cerrar el navegador (filtros de búsqueda). `session` dura lo que dura la
 * pestaña del navegador: sirve para lo que no conviene arrastrar al día siguiente, como la pestaña
 * activa o el mes que se estaba mirando.
 */
export type AlmacenamientoPersistido = 'local' | 'session';

interface OpcionesPersistencia<T> {
    almacenamiento?: AlmacenamientoPersistido;
    // Clave global de una versión anterior (p. ej. `pf_v3`). Se borra al montar para que no quede
    // en el navegador lo que buscó el último usuario que pasó por ese computador.
    claveAnterior?: string;
    // Permite descartar lo guardado al montar si ya no corresponde (p. ej. filtros de otro mes).
    esValido?: (guardado: T) => boolean;
}

const getStorage = (almacenamiento: AlmacenamientoPersistido): Storage =>
    almacenamiento === 'session' ? window.sessionStorage : window.localStorage;

// Hash corto (djb2) para no dejar el RUT del usuario a la vista en el nombre de la clave.
const hashCorto = (texto: string): string => {
    let hash = 5381;
    for (let i = 0; i < texto.length; i++) {
        hash = ((hash << 5) + hash + texto.charCodeAt(i)) | 0;
    }
    return (hash >>> 0).toString(36);
};

/**
 * Agrega a la clave un identificador del usuario logueado, para que en un computador compartido
 * cada uno vea solo lo suyo. Se lee el JWT directo de `localStorage` y no de `AuthContext`: en la
 * primera carga el contexto todavía no tiene al usuario y se leería la clave equivocada.
 */
export const getClaveUsuario = (clave: string): string => {
    try {
        const token = localStorage.getItem('jwt');
        const decodificado = token ? (jwt.decode(token) as JWT | null) : null;
        const usuario = decodificado?.rut || decodificado?.email || decodificado?.sub;
        return `${clave}:${usuario ? hashCorto(usuario) : 'anon'}`;
    } catch {
        return `${clave}:anon`;
    }
};

/**
 * `useState` que se guarda en el navegador por usuario y se recupera al volver a montar la página.
 * El valor se serializa con JSON: no guardar `Date` ni funciones.
 */
export function usePersistedState<T>(
    clave: string,
    valorInicial: T | (() => T),
    opciones: OpcionesPersistencia<T> = {}
): [T, Dispatch<SetStateAction<T>>] {
    const almacenamiento = opciones.almacenamiento || 'local';
    const claveUsuario = useMemo(() => getClaveUsuario(clave), [clave]);

    const [valor, setValor] = useState<T>(() => {
        try {
            const guardado = getStorage(almacenamiento).getItem(claveUsuario);
            if (guardado !== null) {
                const valorGuardado = JSON.parse(guardado) as T;
                if (!opciones.esValido || opciones.esValido(valorGuardado)) {
                    return valorGuardado;
                }
            }
        } catch {
            // Storage bloqueado o JSON corrupto: se parte del valor inicial.
        }
        return typeof valorInicial === 'function' ? (valorInicial as () => T)() : valorInicial;
    });

    useEffect(() => {
        try {
            getStorage(almacenamiento).setItem(claveUsuario, JSON.stringify(valor));
        } catch {
            // Sin storage disponible la página sigue funcionando, solo no recuerda.
        }
    }, [almacenamiento, claveUsuario, valor]);

    useEffect(() => {
        if (!opciones.claveAnterior) return;
        try {
            localStorage.removeItem(opciones.claveAnterior);
        } catch {
            // Nada que limpiar.
        }
    }, [opciones.claveAnterior]);

    return [valor, setValor];
}

export default usePersistedState;
